import {
  fieldAdminListSchema,
  fieldAdminDetailSchema,
  fieldReportHistorySchema,
  fieldAppealSchema,
  fieldReadinessReportSchema,
  fieldAdminRevisionListSchema,
  fieldAdminSourceManifestSchema,
  fieldAdminSourcePageSchema,
} from '@mio/shared';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { apiRequest } from '@/shared/api/client';

import { fieldBlob } from './field-client';

import type {
  FieldModeration,
  fieldAppealCreateSchema,
  fieldAppealReviewSchema,
  fieldAppealResolveSchema,
  FieldDocument,
} from '@mio/shared';
export function useFieldAdminAccess() {
  return useQuery({
    queryKey: ['field-admin-access'],
    queryFn: () => apiRequest('/field/admin/access', z.object({ allowed: z.boolean() })),
    retry: false,
  });
}
export function useFieldAdminList(cursor?: string) {
  return useQuery({
    queryKey: ['field-admin-list', cursor],
    queryFn: ({ signal }) =>
      apiRequest(
        `/field/admin/engagements${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
        fieldAdminListSchema,
        { signal },
      ),
    retry: false,
  });
}
export function useFieldAdminDetail(learnerId: string | null) {
  return useQuery({
    queryKey: ['field-admin-detail', learnerId],
    queryFn: ({ signal }) =>
      apiRequest(`/field/admin/learners/${learnerId}`, fieldAdminDetailSchema, { signal }),
    enabled: Boolean(learnerId),
    retry: false,
  });
}
export function useFieldReports() {
  return useQuery({
    queryKey: ['field-reports'],
    queryFn: ({ signal }) => apiRequest('/field/reports', fieldReportHistorySchema, { signal }),
    retry: false,
  });
}
export const moderateField = (learnerId: string, input: FieldModeration) =>
  apiRequest(`/field/admin/learners/${learnerId}/reviews`, fieldReadinessReportSchema, {
    method: 'POST',
    body: input,
  });
export const beginAppealReview = (
  learnerId: string,
  appealId: string,
  input: z.infer<typeof fieldAppealReviewSchema>,
) =>
  apiRequest(
    `/field/admin/learners/${learnerId}/appeals/${appealId}/review`,
    fieldAdminDetailSchema,
    { method: 'POST', body: input },
  );
export const resolveFieldAppeal = (
  learnerId: string,
  appealId: string,
  input: z.infer<typeof fieldAppealResolveSchema>,
) =>
  apiRequest(`/field/admin/learners/${learnerId}/appeals/${appealId}/resolve`, fieldAppealSchema, {
    method: 'POST',
    body: input,
  });
export function useFieldAppeal() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: z.infer<typeof fieldAppealCreateSchema>) =>
      apiRequest('/field/appeals', fieldAppealSchema, { method: 'POST', body: input }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['field-reports'] }),
  });
}
export async function downloadFieldReport(digest: string) {
  const blob = await fieldBlob(`/reports/${encodeURIComponent(digest)}/download`);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `field-report-${digest.slice(0, 12)}.json`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function useFieldAdminRevisions(learnerId: string, prId: string, offset: number) {
  return useQuery({
    queryKey: ['field-admin-revisions', learnerId, prId, offset],
    queryFn: ({ signal }) =>
      apiRequest(
        `/field/admin/learners/${learnerId}/prs/${prId}/revisions?offset=${offset}`,
        fieldAdminRevisionListSchema,
        { signal },
      ),
    enabled: Boolean(prId),
    retry: false,
  });
}
export function useFieldAdminSourceManifest(learnerId: string, prId: string, revision: string) {
  return useQuery({
    queryKey: ['field-admin-source-manifest', learnerId, prId, revision],
    queryFn: ({ signal }) =>
      apiRequest(
        `/field/admin/learners/${learnerId}/prs/${prId}/revisions/${revision}`,
        fieldAdminSourceManifestSchema,
        { signal },
      ),
    enabled: Boolean(prId && revision),
    retry: false,
  });
}
export function useFieldAdminSourcePage(
  learnerId: string,
  prId: string,
  revision: string,
  path: string,
  page: number,
) {
  return useQuery({
    queryKey: ['field-admin-source-page', learnerId, prId, revision, path, page],
    queryFn: ({ signal }) =>
      apiRequest(
        `/field/admin/learners/${learnerId}/prs/${prId}/revisions/${revision}/file?path=${encodeURIComponent(path)}&page=${page}`,
        fieldAdminSourcePageSchema,
        { signal },
      ),
    enabled: Boolean(prId && revision && path),
    retry: false,
  });
}
export async function downloadFieldAdminDocument(
  learnerId: string,
  document: FieldDocument,
  format: FieldDocument['formats'][number],
) {
  if (!document.formats.includes(format))
    throw new Error('This document representation is not available.');
  const blob = await fieldBlob(
    `/admin/learners/${learnerId}/documents/${encodeURIComponent(document.id)}/download?version=${document.version}&format=${format}`,
  );
  const url = URL.createObjectURL(blob),
    link = window.document.createElement('a');
  link.href = url;
  link.download = `${document.title.replace(/[^a-zA-Z0-9 _-]/g, '').slice(0, 100) || 'document'}-v${document.version}.${format === 'markdown' ? 'md' : format}`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
